import type { ReactNode } from 'react';
import { useScope } from '../lib/scope';
import { cn } from './ui';

/**
 * Einheitlicher Seitenkopf.
 *
 * Jede Seite beginnt gleich aufgebaut: Titel links, Bedienelemente rechts,
 * darunter eine Zeile, die erklaert, was gerade zu sehen ist. Wo der Inhalt vom
 * gewaehlten Bereich abhaengt, steht dieser Bereich dort ausdruecklich - sonst
 * bliebe unklar, warum eine Liste kuerzer ist als erwartet.
 */
export function PageHeader({
  title,
  description,
  /** Zeigt den aktuell gewaehlten Bereich an. Nur dort abschalten, wo die
   *  Seite bereichsunabhaengig ist (z. B. Einstellungen). */
  scoped = true,
  actions,
  className,
}: {
  title: string;
  description?: string;
  scoped?: boolean;
  actions?: ReactNode;
  className?: string;
}): JSX.Element {
  return (
    <div className={cn('flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="min-w-0">
        <h1 className="page-title">{title}</h1>
        <p className="page-subtitle flex flex-wrap items-center gap-x-1.5 gap-y-1">
          {description}
          {scoped && (
            <>
              {description && <span aria-hidden="true">·</span>}
              <ScopeTag />
            </>
          )}
        </p>
      </div>
      {/* max-w-full ist noetig: `shrink-0` allein haelt den Block auf seiner
          Inhaltsbreite fest, sodass vier Schaltflaechen am Smartphone die
          ganze Seite seitlich ueberlaufen lassen. Mit der Begrenzung greift
          stattdessen der Umbruch. */}
      {actions && <div className="flex max-w-full shrink-0 flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Der aktuell gewaehlte Bereich als Kurzhinweis.
 * Bewusst dezent - der Umschalter selbst sitzt in der Kopfzeile.
 */
function ScopeTag(): JSX.Element {
  const scope = useScope();
  const isAll = scope.unitId === null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-xs font-medium',
        isAll ? 'bg-slate-100 text-slate-600' : 'bg-brand-50 text-brand-700',
      )}
      title={
        isAll
          ? 'Es sind alle Bereiche sichtbar'
          : `Eingeschränkt auf ${scope.breadcrumb}${scope.includeChildren ? ' und untergeordnete Bereiche' : ''}`
      }
    >
      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M6.429 9.75L2.25 12l4.179 2.25m0-4.5l5.571 3 5.571-3m-11.142 0L2.25 7.5 12 2.25l9.75 5.25-4.179 2.25m0 0L21.75 12l-4.179 2.25m0 0l4.179 2.25L12 21.75 2.25 16.5l4.179-2.25"
        />
      </svg>
      {scope.breadcrumb}
    </span>
  );
}
